import { createContext } from 'react';

import type { SessionKey } from '@infinex/main-service-contract/v2/server/user';

import type { Session } from './types';

export type SessionContextType = {
  /**
   * Session object:
   * - undefined if still loading (unsure logged in or not)
   * - null if the user is absolutely not logged in
   * (undefined vs. null is important to ensure useOnSessionChange() gives at least one update even if logged out)
   */
  session: Session | null | undefined;

  /**
   * Promise that resolves when we get the initial session status.
   * This promise resolves once and never changes. Use this to guard downstream
   * operations that need to wait for the initial session check.
   */
  sessionResolved: Promise<void>;

  /**
   * Has initial check for user status completed.
   * (duplicate for convenience: isLoading = session === undefined)
   */
  isLoading: boolean;

  /**
   * User login status.
   * - undefined while we don't know the status
   * - boolean when status is confirmed.
   * (duplicate for convenience: isLoggedIn = isLoading ? undefined : !!session)
   */
  isLoggedIn: boolean | undefined;

  /**
   * Session key information from ping response.
   * Contains expiry and turnkey user type.
   */
  sessionKey: SessionKey | null;

  /**
   * Worker version from ping response.
   */
  workerVersion: string | null;

  /**
   * Callback to be called when the user logs in.
   */
  onLogin: (session: Session) => void;

  /**
   * Callback to be called when the user logs out.
   */
  onLogout: () => void;
};

const defaultSessionResolved = Promise.reject(
  new Error('sessionResolved must be used within a SessionProvider')
);
defaultSessionResolved.catch(() => {
  /* avoid unhandledRejection */
});

export const SessionContext = createContext<SessionContextType>({
  session: undefined,
  sessionResolved: defaultSessionResolved,
  isLoading: true,
  isLoggedIn: undefined,
  sessionKey: null,
  workerVersion: null,
  onLogin: () => {
    throw new Error('onLogin must be used within a SessionProvider');
  },
  onLogout: () => {
    throw new Error('onLogout must be used within a SessionProvider');
  },
});
